import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ADD_NEW_CARD,
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL,
    NEW_OUTSTANDING_PAYMENT,
    Party,
    PaymentCard,
    Policy,
    POLICY_STATUS_RENEWAL,
    PolicyProperty,
    PolicyPropertyEnum,
    PROCESS_PAY_OFF_BALANCE,
    productHelper,
    ProductsEnum, Renewal,
    SETUP_DIRECT_DEBIT,
    Subscription,
    Types,
    WOLF_MISSED_PAYMENT,
} from "@falcon/services";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {PaymentHistoryTable} from "../PaymentHistoryTable";
import {PaymentDaySection} from "../PaymentDaySection";
import {StorageSuccessModal} from "../../../../../../components/modals/StorageSuccessModal";
import {AutomaticRenewalCheckboxSection} from "./AutomaticRenewalCheckboxSection";
import {PaymentScheduleSection} from "./PaymentScheduleSection";
import {AccountBalanceSection} from "./AccountBalanceSection";
import {
    calculateInstallmentPayment,
    findMainAnnualPaymentCardByMainCardId,
    getAmountToDisplay,
    getNextInstalmentDate,
    getProductByPolicyNumber,
    renderCardTypeName
} from "../../../../../../CommonFunctions";
import * as moment from "moment";
import {CardExpiryBanner} from "../../../../../../components/banners/CardExpiryBanner";
import {RouteComponentProps} from "react-router-dom";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../../../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {OverdueInstalmentBanner} from "../../../../../../components/banners/OverdueInstalmentBanner";
import {NextPaymentBanner} from "../../../../../../components/banners/NextPaymentBanner";
import {AnnualPaymentCardsSection} from "./AnnualPaymentCardsSection";
import {DirectDebitSection} from "./DirectDebitSection";
import {InactiveDirectDebitBanner} from "../../../../../../components/banners/InactiveDirectDebitBanner";
import {OutstandingPaymentBanner} from "../../../../../../components/banners/OutstandingPaymentBanner";
import {DirectDebitRenewalPaymentPlanOutput} from "../DirectDebitRenewalPaymentPlanOutput";

interface IMonthlyPaymentsDetailsPageParams {
    policyNumber: string;
}

interface IMonthlyPaymentsDetailsPageProps extends RouteComponentProps<IMonthlyPaymentsDetailsPageParams> {
    location: any;
}

interface IState {
    policies?: Policy[],
    subscription?: Subscription,
    party?: Party,
    policy?: Policy,
    policiesLoaded: boolean,
    subscriptionLoaded: boolean,
    partyLoaded: boolean,
    instalmentsInfo?: InstalmentsInfo,
    missedPaymentInfo?: InstalmentsInfo,
    showCardExpiryBanner: boolean,
    paymentDay: string,
    renewedPolicy: boolean,
    policiesProcessed: boolean,
    cardExpired?: boolean,
    mainCardId?: string,
    allPoliciesAreCancelled: boolean,
    isMobileScreenSize: boolean,
    canChangePaymentDate: boolean,
    paymentType: string,
    reuseCard: boolean,
    renewalProposal?: any,
    haveMandates: boolean,
    mandate?: any,
}

@authorize
export class MonthlyPaymentsDetailsPage extends React.Component<IMonthlyPaymentsDetailsPageProps, IState> {

    private pluginTemplates = new PluginTemplates();
    private title: string;
    private modalContent: JSX.Element;
    private anythingElseTitle: any;
    private explanationAnythingElseWording: any;
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;
    private installmentSum: number = 0;
    private readonly product: ProductsEnum;
    private readonly policyNumber: string;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    constructor(props: IMonthlyPaymentsDetailsPageProps) {
        super(props);

        this.state = {
            policiesLoaded: false,
            subscriptionLoaded: false,
            partyLoaded: false,
            showCardExpiryBanner: false,
            renewedPolicy: false,
            policiesProcessed: false,
            paymentDay: "",
            allPoliciesAreCancelled: true,
            isMobileScreenSize: window.innerWidth < 768,
            canChangePaymentDate: true,
            paymentType: "",
            reuseCard: false,
            haveMandates: false,
        };

        this.pluginTemplates.setPageTitle("Payment details");

        this.policyNumber = this.props.match.params.policyNumber;
        this.product = getProductByPolicyNumber(this.policyNumber);
    }

    public async componentDidMount() {
        this.cacheManager.clearCache("getPolicies");
        this.cacheManager.clearCache(`getPolicyPropertyByPropertyKey${this.policyNumber},modifiedPaymentDay`);
        this.cacheManager.clearCache("getProfile");

        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");
        if (this.product === ProductsEnum.HOME && !this.showSuccessPopup) {
            window.sessionStorage.removeItem("paymentAmount");
            window.sessionStorage.removeItem("cardNumberObfuscated");
        }

        this.customerPortalService.getPolicies(this.product, undefined, true)
            .subscribe((policies: Policy[]) => {
                const sortedPolicies: Policy[] = policies.filter((policy: Policy) => policy.products.includes(this.product))
                    .sort((a, b) => moment(b.endDate).diff(a.endDate));

                const allPoliciesAreCancelled = !productHelper.atLeastOneActivePolicy(sortedPolicies);
                this.setState({allPoliciesAreCancelled});

                const filteredPolicies: Policy[] = sortedPolicies
                    .filter((policy: Policy) => policy.products.includes(this.product) && policy.policyNumber === this.policyNumber)
                    .sort((a, b) => moment(b.endDate).diff(a.endDate));

                if (filteredPolicies && filteredPolicies.length > 0) {
                    const policy = filteredPolicies[0];
                    const agreementVersionIds: number[] = policy.policyHistoryList.map(ph => ph.id);

                    this.customerPortalService.getPolicyPropertyByPropertyKey(policy.policyNumber, "modifiedPaymentDay").subscribe(
                        (property: PolicyProperty) => {
                            if (property !== null) {
                                this.setState({paymentDay: property.value});
                            } else {
                                this.setState({paymentDay: policy.interaction.paymentDay.day});
                            }
                        }
                    );

                    const annualPolicyInfo: AnnualPolicyInfo | undefined = policy.annualPolicyInfo || undefined;
                    let mandates = null;
                    if (annualPolicyInfo?.mandates) {
                        mandates = annualPolicyInfo.mandates.filter(
                            (entry: any) => entry.status === "NEW"
                                || entry.status === "REQUESTED"
                                || entry.status === "VALID");
                        this.setState({
                            haveMandates: mandates.length > 0
                        })
                        if (this.state.haveMandates) {
                            mandates.sort((firstMandate: any, secondMandate: any) => secondMandate.id - firstMandate.id);
                            this.setState({
                                mandate: mandates[0]
                            })
                        }
                    }

                    this.setState({
                        policy
                    });

                    this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds)
                        .subscribe((instalmentsInfo: any) => {
                            this.setState({instalmentsInfo});
                        });

                    if (policy?.interaction?.factors?.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL &&
                        policy?.interaction?.renewalDetails?.policyAgeYears > 0) {
                        this.customerPortalService.getMissedPaymentInfo(policy.interaction.agreementVersionId).subscribe(
                            (missedPaymentInfo: InstalmentsInfo) => {
                                this.setState({missedPaymentInfo});
                            });
                    }

                    // is policy is renewed
                    const renewedPolicy: boolean = sortedPolicies[0].policyHistoryList.filter(ph => ph.status === POLICY_STATUS_RENEWAL).length > 0;

                    this.setState({
                        renewedPolicy,
                        policies: sortedPolicies,
                        policiesProcessed: true
                    });
                } else {
                    this.setState({
                        policiesProcessed: true
                    });
                }

                this.setState({
                    policiesLoaded: true
                });
            });

        this.customerPortalService.getSubscription().subscribe((subscription: Subscription) => {
            this.setState({
                subscription,
                subscriptionLoaded: true
            });
        });

        this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.policyNumber, [PolicyPropertyEnum.ANNUAL_MONTHLY_OPTION_CARD_EXPIRED, PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.REUSE_MAIN_CARD]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === PolicyPropertyEnum.ANNUAL_MONTHLY_OPTION_CARD_EXPIRED) {
                        this.setState({cardExpired: property.value === 'true'});
                    } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                        this.setState({mainCardId: property.value})
                    } else if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                        this.setState({reuseCard: property.value === 'true'})
                    }
                })
            }
        );

        this.customerPortalService.getPolicyPaymentTypeByPolicyNumber(this.policyNumber).subscribe((paymentType: any) => {
            this.setState({
                paymentType
            });
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true
            })
        });

        this.customerPortalService.isApplicableForPaymentDayChange(this.props.match.params.policyNumber).subscribe((output: boolean) => {
            this.setState({
                canChangePaymentDate: output
            })
        })

        this.customerPortalService.getRenewalProposal(this.props.match.params.policyNumber).subscribe((renewalProposal: Renewal) => {
            if (renewalProposal?.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY) {
                this.setState({
                    renewalProposal
                })
            }
        })
    }

    public render() {
        const loading: boolean = !(this.state.policiesLoaded && this.state.subscriptionLoaded && this.state.partyLoaded && this.state.policiesProcessed);
        const annualPolicyInfo: AnnualPolicyInfo | undefined = this.state.policy ? this.state.policy.annualPolicyInfo : undefined;
        const instalmentsInfo: InstalmentsInfo | undefined = this.state.missedPaymentInfo ?? this.state.instalmentsInfo;
        const canChangePaymentDate: boolean | undefined = this.state.canChangePaymentDate;
        const isDirectDebitPaymentType: boolean = this.state.paymentType === "directDebit";

        this.setPopups();

        const lastPolicyInteraction = this.getLastPolicyInteraction();
        const showCardExpiryBanner: boolean = this.state.cardExpired !== undefined;

        let activePolicy: any = null;
        let product: any = null;
        let payOffInstallmentPresent: boolean = false;

        if (instalmentsInfo?.instalments !== undefined) {
            payOffInstallmentPresent = instalmentsInfo.instalments.filter((instalment) => instalment.description.includes("PayOffBalance")).length > 0;
        }

        if (this.state.policiesLoaded && this.state.policies) {
            if (this.state.policies?.some(policy => productHelper.isWolfMotor(policy.interaction))) {
                activePolicy = productHelper.getActiveAnnualMotorPolicy(this.state.policies, this.policyNumber);
                product = "annualMotor";
            } else {
                activePolicy = productHelper.getActiveAnnualHomePolicy(this.state.policies, this.policyNumber)
                product = "annualHome";
            }
        }

        let haveActiveMandate: boolean = false;
        let mandate = null;
        if (annualPolicyInfo && annualPolicyInfo.mandates && annualPolicyInfo.mandates.length > 0) {
            const mandates = annualPolicyInfo.mandates.sort((firstMandate: any, secondMandate: any) => secondMandate.id - firstMandate.id);
            mandate = mandates[0];
            haveActiveMandate = mandate.status === "REQUESTED" || mandate.status === "NEW" || mandate.status === "VALID";
        }

        this.installmentSum = calculateInstallmentPayment(this.state.instalmentsInfo?.instalments);
        const showOverdueInstalmentBanner: boolean = !!annualPolicyInfo && annualPolicyInfo.hasDebt;
        const isDirectDebit: boolean = this.state.renewalProposal && this.state.renewalProposal.paymentDetails.paymentType === "directDebit"

        return (

            <div className="dashboard payment-details">

                {this.showSuccessPopup &&
                    <StorageSuccessModal
                        title={this.title}
                        explanationWording={this.modalContent}
                        anythingElseTitle={this.anythingElseTitle}
                        explanationAnythingElseWording={this.explanationAnythingElseWording}
                        onClose={this.modalOnClose}
                    />
                }

                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={{
                            link: `/dashboard/${product}/${this.state.policy?.policyNumber}`,
                            hidden: !this.state.policy
                        }}
                        title="Payment details"
                    />
                    {this.state.policy && !!this.state.policy.annualPolicyInfo.unpaidChargebackInvoiceAmount && this.referrerJourney !== NEW_OUTSTANDING_PAYMENT &&
                        <OutstandingPaymentBanner
                            amount={this.state.policy.annualPolicyInfo.unpaidChargebackInvoiceAmount}
                            policyNumber={this.state.policy.policyNumber}
                            product={this.state.policy.interaction.product}
                        />
                    }

                    {showOverdueInstalmentBanner && annualPolicyInfo &&

                            <OverdueInstalmentBanner amount={annualPolicyInfo.overdueMonthlyInvoiceAmount}
                                                     unpaidInstallmentIdToAmount={annualPolicyInfo.unpaidInstallmentIdToAmount}
                                                     policyNumber={lastPolicyInteraction.agreementNumber}
                                                     product={this.product}
                                                     ncdImpactedClaim={this.state.policy?.interaction.uistate.policyHasNcdImpactedClaims}
                                                     isDirectDebit={isDirectDebitPaymentType}
                                                        reuseCard={this.state.reuseCard}/>
                        }


                    {(!showOverdueInstalmentBanner || !annualPolicyInfo?.overdueMonthlyInvoiceAmount) && instalmentsInfo && (
                        <NextPaymentBanner showInfoModal={false} paymentDate={getNextInstalmentDate(instalmentsInfo)}
                                           instalmentsInfo={instalmentsInfo} smallScreenMarginRequired={true}/>
                    )}
                    {lastPolicyInteraction && isDirectDebitPaymentType && !haveActiveMandate  && this.state.instalmentsInfo != null && Math.abs(this.state.instalmentsInfo.payOffAmount!) > 0 && (
                        <InactiveDirectDebitBanner policyNumber={this.policyNumber} smallScreenMarginRequired={true}/>
                    )}

                    {this.state.party && showCardExpiryBanner &&
                        <div className="inner-container">
                            <CardExpiryBanner
                                party={this.state.party!}
                                policyNumber={this.policyNumber}
                                annualMonthlyOptionCardExpired={this.state.cardExpired!}
                            />
                        </div>}

                    <div className="panel bg-white overflow-hidden p-0 mx-3 font-weight-extra-light">
                        <div className="inner-content pb-4 payment-details-table">
                            {this.state.policiesLoaded && !(this.state.instalmentsInfo?.remainingAmount === 0) &&
                                <PaymentDaySection allPoliciesAreCancelled={this.state.allPoliciesAreCancelled}
                                                   renewalPaymentFailed={this.state.subscription?.renewalPaymentFailed}
                                                   interaction={lastPolicyInteraction}
                                                   paymentType="annual-product-monthly-payments"
                                                   paymentDay={this.state.paymentDay}
                                                   canChangePaymentDate={canChangePaymentDate}/>
                            }

                            <div className="dashed-10"/>

                            {this.state.policiesLoaded && isDirectDebitPaymentType && !payOffInstallmentPresent &&
                                <DirectDebitSection haveActiveMandate={haveActiveMandate}
                                                    policyNumber={this.props.match.params.policyNumber}
                                                    interaction={lastPolicyInteraction} mandate={mandate}/>
                            }
                            <div className="dashed-10"/>
                            <AnnualPaymentCardsSection policyNumber={this.policyNumber}
                                                       payment="monthly"
                            handleReuseCardChange={this.handleReuseCardChange}/>

                            {this.state.policiesLoaded && isDirectDebit &&
                                <>
                                    <div className="dashed-10"/>
                                    <PaymentDaySection
                                        allPoliciesAreCancelled={this.state.allPoliciesAreCancelled}
                                        interaction={this.state.renewalProposal}
                                        paymentType="annual-product-monthly-payments"
                                        paymentDay={this.getPaymentDay()}
                                        canChangePaymentDate={true}
                                        renewalPaymentFailed={false}
                                        annualToMonthly={true}/>
                                </>
                            }

                            {this.state.policiesLoaded && this.state.mandate && isDirectDebit &&
                                <>
                                    <div className="dashed-10"/>
                                    <DirectDebitSection haveActiveMandate={this.state.haveMandates}
                                                        policyNumber={this.props.match.params.policyNumber}
                                                        interaction={this.state.renewalProposal}
                                                        mandate={this.state.mandate}
                                                        annualToMonthly={true}/>
                                </>
                            }

                            <div className="dashed-10"/>

                            {this.state.policiesLoaded && this.state.mandate && this.state.renewalProposal && isDirectDebit &&
                                <div id="paymentScheduleTable">
                                    <DirectDebitRenewalPaymentPlanOutput
                                        interaction={this.state.renewalProposal}/>
                                </div>
                            }

                            {this.state.policiesLoaded && this.state.policies && !isDirectDebit &&
                                <PaymentScheduleSection
                                    paidAmount={this.state.instalmentsInfo?.paidAmount}
                                    installmentSum={this.installmentSum}
                                    instalments={this.state.instalmentsInfo?.instalments || []}
                                    totalAmount={this.state.instalmentsInfo?.totalAmount}
                                    interaction={this.state.policies![0].interaction}
                                    isPaidOff={this.state.instalmentsInfo?.remainingAmount === 0}
                                />
                            }
                            <div className="dashed-10"/>

                            {lastPolicyInteraction != null
                                && this.state.instalmentsInfo != null
                                && this.state.instalmentsInfo.remainingPremium! > 0
                                && this.state.instalmentsInfo.payOffAmountWithRemainingInterest! > 0 &&
                                <AccountBalanceSection
                                    interaction={lastPolicyInteraction}
                                    policyNumber={this.props.match.params.policyNumber}
                                    payOffAmountWithRemainingInterest={this.state.instalmentsInfo.payOffAmountWithRemainingInterest}
                                    remainingInterest={this.state.instalmentsInfo.remainingInterest}
                                />}

                            {!this.state.renewedPolicy
                                && <div className="dashed-10"/>
                                &&
                                <>
                                    <div className="blue-line"/>
                                    <PaymentHistoryTable
                                        subscription={this.state.subscription}
                                        showProduct={this.product}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.all}
                                        paymentType="monthly"
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                </>}


                            {this.state.renewedPolicy
                                &&
                                <>
                                    <div className="dashed-10"/>
                                    <PaymentHistoryTable
                                        title="Existing policy payment history"
                                        subscription={this.state.subscription}
                                        showProduct={this.product}
                                        expanded={true}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.current}
                                        paymentType="monthly"
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                    <div className="dashed-10"/>
                                    <PaymentHistoryTable
                                        title="Old policies payment history"
                                        subscription={this.state.subscription}
                                        showProduct={this.product}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.past}
                                        paymentType="monthly"
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                </>
                            }

                        </div>
                    </div>

                    {activePolicy &&
                        <AutomaticRenewalCheckboxSection
                            history={this.props.history}
                            activeAnnualPolicy={activePolicy}
                            product={this.product}
                        />}

                    {this.state.policy && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/${product}/${this.state.policy.policyNumber}`)}
                </div>

                <FalconFooter/>
                <Overlay show={loading}/>
            </div>
        );
    }
    private getLastPolicyInteraction(): any {
        return this.state.policy ? this.state.policy.interaction : null;
    }

    private getPaymentDay(): any {
        return this.state.haveMandates ? (this.state.renewalProposal?.paymentDetails?.directDebitDetails?.paymentDay ?? this.state.renewalProposal.paymentDay.day) : this.state.renewalProposal.paymentDay.day
    }

    private modalOnClose(): void {
        window.sessionStorage.removeItem("paymentAmount");
        window.sessionStorage.removeItem("cardNumberObfuscated");
    }

    private setPaymentDayChangePopup = (): void => {
        if (this.state.policiesLoaded && this.state.policies) {
            const paymentDay: string = this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY
                ? this.state.paymentDay
                : this.state.renewalProposal?.paymentDay?.day ?? '';
            this.title = "All done!";
            this.modalContent =
                <div>
                    <p>
                        You have a new payment day,
                        every {PluginTemplates.convertWolfPaymentDate(paymentDay)} of the month.
                    </p>
                </div>


            this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
            this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
        }
    }

    private setManualPaymentSuccessPopup = (): void => {
        let cardNumberObfuscated = window.sessionStorage.getItem("cardNumberObfuscated");
        if (!cardNumberObfuscated) {
            cardNumberObfuscated = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!)?.cardNumberObfuscated!;
        }
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <span className="font-weight-bold"
                      id={"paymentAmount"}>{window.sessionStorage.getItem("paymentAmount")}&nbsp;</span>
                have been debited from your card ending <b> {cardNumberObfuscated?.split('******')[1]} </b> to pay your overdue instalment
            </p>;
        this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
        this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
    }

    private setNewCardPopup(): void {
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (paymentCard) {
            this.title = "All done!";
            this.modalContent =
                <p className="font-weight-bold">
                    You have a new payment method, {renderCardTypeName(paymentCard)} card ending
                    in {paymentCard.cardNumberObfuscated.split('******')[1]}
                </p>;
            this.anythingElseTitle = "";
            this.explanationAnythingElseWording = "";
        }

    }

    private setDirectDebitSetupPopup(): void {
        this.title = "All done!";
        this.modalContent =
            <p>
                Your new Direct Debit details have been updated along with your Direct Debit reference. You'll be able
                to see a new payment plan in your 'Documents' tile shortly.
            </p>;
        this.anythingElseTitle = "";
        this.explanationAnythingElseWording = "";
    }

    private setPayOffBalancePopup(): void {
        const paymentAmount: string | null = window.sessionStorage.getItem("paymentAmount");
        let activePolicy: any = null;
        if (this.state.policiesLoaded && this.state.policies) {
            if (this.state.policies?.some(policy => productHelper.isWolfMotor(policy.interaction))) {
                activePolicy = productHelper.getActiveAnnualMotorPolicy(this.state.policies, this.policyNumber);
            } else {
                activePolicy = productHelper.getActiveAnnualHomePolicy(this.state.policies, this.policyNumber)
            }

            this.title = "Payment was successful";
            this.modalContent = (
                <>
                    <p className="mb-0">
                        {paymentAmount ?? getAmountToDisplay(activePolicy.interaction.factors.payOffPremium)} has been
                        taken out
                        from your account for paying off
                        your {productHelper.isWolfMotor(activePolicy.interaction) ? "car" : "home"} insurance.
                    </p>
                    <p>
                        Please allow a couple of minutes for the payment to be updated in your account.
                    </p>
                </>
            )
        }
    }

    private setChargeBackPayoff(): void {
        window.sessionStorage.setItem("outstandingPaid", "true");
        const paymentAmount: string | null = window.sessionStorage.getItem("paymentAmount");
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (this.state.policiesLoaded) {
            this.title = "Payment was successful";
            this.modalContent = (
                <p className="mb-0">
                    <span className="font-weight-bold">{paymentAmount}</span> has been debited from your card
                    ending <span className="font-weight-bold">{paymentCard && Boolean(paymentCard) ? paymentCard.cardNumberObfuscated.split('******')[1] : '****'}</span> to pay your <span className="font-weight-bold">outstanding balance</span>.
                </p>
            )
        }
    }


    private setPopups(): void {
        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");

        if (this.state.partyLoaded && this.state.policies) {
            if (this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY
                || this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL) {
                this.setPaymentDayChangePopup();
            } else if (this.referrerJourney === WOLF_MISSED_PAYMENT) {
                this.setManualPaymentSuccessPopup();
            } else if (this.referrerJourney === ADD_NEW_CARD) {
                this.setNewCardPopup();
            } else if (this.referrerJourney === SETUP_DIRECT_DEBIT ||
                this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT) {
                this.setDirectDebitSetupPopup();
            } else if (this.referrerJourney === NEW_OUTSTANDING_PAYMENT) {
                this.setChargeBackPayoff();
            } else if (this.referrerJourney === PROCESS_PAY_OFF_BALANCE || this.referrerJourney === INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE) {
                this.setPayOffBalancePopup();
            } else {
                window.sessionStorage.removeItem("showPopup");
                window.sessionStorage.removeItem("referrerJourney");
            }
        }
    }

    private handleReuseCardChange = (reuseValue: boolean) => {
        this.setState({reuseCard: reuseValue})
}
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/paymentDetails/annual/MonthlyPaymentsDetailsPage.tsx